import { exchangeApi } from './exchangeApi'
import { exchangeToast, type ExchangeToastAction } from './exchangeToasts'
import { fullName, rowDateLabel, shortName, timeRange, toLocal, dayKeyOf } from './exchangeFormat'
import { toggleIn, useExchangePage, type ExchangePage } from './exchangePage'
import type { ExchangeError, UserVacancy, Vacancy } from './exchangeTypes'

// Понятные тексты кодов target-contract §1/§6 и уточнений RD-165; иначе — сообщение сервера или общий текст.
// Реакция определяется по code, не по одному HTTP-статусу 409.
const CODE_TEXT: Record<string, string> = {
    response_withdrawn: 'Сотрудник отозвал отклик. Данные обновлены.',
    employee_shift_conflict: 'У сотрудника уже есть смена в это время. Данные обновлены.',
    vacancy_already_closed: 'Вакансия уже закрыта.',
    vacancy_start_passed: 'Начало смены уже наступило — действие недоступно.',
    vacancy_edit_locked: 'По вакансии уже были отклики — изменить её нельзя.',
    vacancy_has_shift: 'Сотрудник назначен и смена создана — снять вакансию нельзя.',
    deletion_reason_required: 'Укажите причину снятия.',
    not_found: 'Запись уже удалена. Данные обновлены.',
    dates_limit_exceeded: 'Не больше 100 дат в одном пакете.',
    idempotency_key_required: 'Не удалось отправить пакет — попробуйте ещё раз.',
    idempotency_key_reused: 'Пакет уже отправлялся с другими условиями — проверьте список и отправьте заново.',
}

export function errorText(error: unknown, fallback: string): string {
    const e = error as ExchangeError | null
    if (e?.kind === 'network') return 'Не удалось получить ответ сервера — проверьте результат в списке.'
    if (e?.code && CODE_TEXT[e.code]) return CODE_TEXT[e.code]
    return e?.message || fallback
}

// Конкретное время чужой смены — только из тела ошибки employee_shift_conflict (timestamp с offset → зона подразделения).
function conflictText(error: ExchangeError, user: Pick<UserVacancy, 'first_name' | 'last_name'> | null): string | null {
    const shift = (error.body as { conflicting_shift?: { dttm_work_start_at?: string, dttm_work_end_at?: string } } | null)?.conflicting_shift
    if (!shift?.dttm_work_start_at || !shift.dttm_work_end_at) return null
    const time = `${toLocal(shift.dttm_work_start_at).format('HH:mm')}–${toLocal(shift.dttm_work_end_at).format('HH:mm')}`
    return user ? `У ${shortName(user)} уже есть смена ${time}` : `У сотрудника уже есть смена ${time}`
}

// Единая блокировка по ID вакансии для строки, панели, диалога снятия и страницы (spec «Списки…» п.5):
// пока выполняется действие и последующее обновление данных, конфликтующие действия той же вакансии недоступны.
// Компонент, создавший состояние раздела (Exchange.vue), передаёт его явно: inject своего же provide не видит.
export function useExchangeGuard(page: ExchangePage = useExchangePage()) {

    const isBusy = (id: string) => page.busy.includes(id)

    async function guarded<T>(id: string, work: () => Promise<T>): Promise<T | undefined> {
        if (isBusy(id)) return undefined
        page.busy = toggleIn(page.busy, id, true)
        try {
            return await work()
        } finally {
            page.busy = toggleIn(page.busy, id, false)
        }
    }

    return { isBusy, guarded }
}

export function useExchangeActions(refresh: () => Promise<void> | void) {
    const { isBusy, guarded } = useExchangeGuard()

    const when = (vacancy: Vacancy) => `${rowDateLabel(dayKeyOf(vacancy.start))} · ${timeRange(vacancy).replace(' – ', '–')}`

    async function confirmResponse(vacancy: Vacancy, user: UserVacancy) {
        await guarded(vacancy.id, async () => {
            try {
                await exchangeApi.confirm(vacancy.id, user.id)
                exchangeToast.success('Сотрудник назначен', { text: `${fullName(user)} · ${when(vacancy)}`, key: `confirm:${vacancy.id}` })
            } catch (error) {
                const e = error as ExchangeError
                const text = e?.code === 'employee_shift_conflict' ? conflictText(e, user) : null
                exchangeToast.error('Не удалось назначить', { text: text ?? errorText(error, 'Попробуйте ещё раз'), key: `confirm:${vacancy.id}` })
            }
            await refresh()
        })
    }

    async function rejectResponse(vacancy: Vacancy, user: UserVacancy) {
        await guarded(vacancy.id, async () => {
            try {
                await exchangeApi.rejectResponse(vacancy.id, user.id)
                exchangeToast.success('Отклик отклонён', { text: fullName(user), key: `reject:${vacancy.id}` })
            } catch (error) {
                exchangeToast.error('Не удалось отклонить отклик', { text: errorText(error, 'Попробуйте ещё раз'), key: `reject:${vacancy.id}` })
            }
            await refresh()
        })
    }

    async function rejectRequest(vacancy: Vacancy) {
        await guarded(vacancy.id, async () => {
            try {
                await exchangeApi.rejectRequest(vacancy.id)
                exchangeToast.success('Запрос отклонён', { text: `${vacancy.position.name} · ${when(vacancy)}`, key: `request:${vacancy.id}` })
            } catch (error) {
                exchangeToast.error('Не удалось отклонить запрос', { text: errorText(error, 'Попробуйте ещё раз'), key: `request:${vacancy.id}` })
            }
            await refresh()
        })
    }

    // Согласование запроса — существующее назначение requested_user. Ошибка не превращает запрос в отклонённый.
    async function approveRequest(vacancy: Vacancy) {
        const user = vacancy.requestedUser
        const userId = vacancy.requestedUserId
        if (!userId) return
        await guarded(vacancy.id, async () => {
            try {
                await exchangeApi.confirm(vacancy.id, userId)
                exchangeToast.success('Запрос согласован', { text: `${fullName(user)} · ${when(vacancy)}`, key: `request:${vacancy.id}` })
            } catch (error) {
                const e = error as ExchangeError
                const conflict = e?.code === 'employee_shift_conflict' ? conflictText(e, user) : null
                const action: ExchangeToastAction | null = conflict ? { label: 'Отклонить запрос', run: () => { void rejectRequest(vacancy) } } : null
                exchangeToast.error('Не удалось согласовать', { text: conflict ?? errorText(error, 'Попробуйте ещё раз'), action, key: `request:${vacancy.id}` })
            }
            await refresh()
        })
    }

    return { isBusy, guarded, confirmResponse, rejectResponse, rejectRequest, approveRequest }
}
